import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, InputNumber, Button, message, Select, Cascader, notification } from 'antd';
import axios from 'axios';
import {instance} from "../../API/API";

const { Option } = Select;

// Создаем экземпляр axios

const AddProductModal = ({ visible, onClose, onAdd, categories }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Список единиц измерения
    const unitOptions = [
        { value: 'шт', label: 'шт' },
        { value: 'Kg', label: 'Kg' },
        { value: 'g', label: 'g' },
        { value: 'L', label: 'L' },
        { value: 'ml', label: 'ml' },
        { value: 'M', label: 'M' },
        { value: 'sm', label: 'sm' },
        { value: 'M2', label: 'M²' }
    ];

    // Преобразуем категории в формат для Cascader
    const categoryOptions = useMemo(() => {
        if (!categories?.mainCategory) return [];

        return categories.mainCategory.map(mainCat => ({
            value: mainCat.id,
            label: mainCat.name,
            children: categories.subCategory
                .filter(subCat => subCat.parent_id === mainCat.id)
                .map(subCat => ({
                    value: subCat.id,
                    label: subCat.name
                }))
        }));
    }, [categories]);

    const handleSubmit = async (values) => {
        setLoading(true);
        const productName = values.name || 'Новая позиция';

        try {
            const formData = new FormData();

            // Если выбрана каскадная категория [main, sub]
            if (Array.isArray(values.category) && values.category.length === 2) {
                formData.append('postSubCat', values.category[1]);
            } else {
                message.error('Выберите категорию');
                setLoading(false);
                return;
            }

            formData.append('postBrand', values.brand);
            formData.append('postName', values.name);
            formData.append('postCost', values.cost);
            formData.append('postCount', values.store_1);
            formData.append('postDescription', values.description || '');
            formData.append('postPackage_name', values.unit || '');
            formData.append('postPack', values.package || 0);
            formData.append('postDate_avalible', values.date_available || '');

            // Отправляем через axios
            const response = await instance.post('store/post_new.php', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }, withCredentials: true
            });

            if (response.data.success) {
                // Успешное уведомление
                onAdd(response.data.id);
                onClose();
            } else {
                const errorMsg = response.data.error || 'Неизвестная ошибка';
                // Уведомление об ошибке
                notification.error({
                    message: 'Ошибка при добавлении позиции',
                    description: `Позиция "${productName}" не была добавлена. ${errorMsg}`,
                    placement: 'topRight',
                    duration: 4,
                });
                message.error(errorMsg);
            }
        } catch (error) {
            let errorMessage = '';

            if (error.response) {
                // Сервер ответил с ошибкой
                errorMessage = error.response.data?.error || 'Ошибка сервера';
            } else if (error.request) {
                // Запрос был сделан, но ответ не получен
                errorMessage = 'Ошибка сети: нет ответа от сервера';
            } else {
                // Произошла ошибка при настройке запроса
                errorMessage = error.message || 'Ошибка при отправке запроса';
            }

            // Уведомление об ошибке
            notification.error({
                message: 'Ошибка при добавлении позиции',
                description: `Позиция "${productName}" не была добавлена. ${errorMessage}`,
                placement: 'topRight',
                duration: 4,
            });

            message.error(errorMessage);
            console.error('Error adding product:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Добавить новую позицию"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="category"
                    label="Категория"
                    rules={[{ required: true, message: 'Выберите категорию' }]}
                >
                    <Cascader
                        options={categoryOptions}
                        placeholder="Выберите основную и подкатегорию"
                        showSearch={{
                            filter: (inputValue, path) =>
                                path.some(option =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                ),
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="brand"
                    label="Бренд"
                    rules={[{ required: true, message: 'Введите бренд' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Введите название' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="cost"
                    label="Цена"
                    rules={[{ required: true, message: 'Введите цену' }]}
                >
                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="store_1"
                    label="Количество на складе (Будва)"
                    rules={[{ required: true, message: 'Введите количество' }]}
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                >
                    <Input.TextArea rows={3} />
                </Form.Item>

                <Form.Item
                    name="unit"
                    label="Единица измерения"
                >
                    <Select placeholder="Выберите единицу измерения" allowClear>
                        {unitOptions.map(unit => (
                            <Option key={unit.value} value={unit.value}>
                                {unit.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="package"
                    label="Количество в упаковке"
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    name="date_available"
                    label="Годен до:"
                >
                    <Input type="date" />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 8 }}>
                        Добавить
                    </Button>
                    <Button onClick={onClose}>
                        Отмена
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddProductModal;